import CartaoErro from '../../componentes/CartaoErro'
import { sinal, rotuloDoAjuste, valorComSinal } from '../../comissoes/sinalDoAjuste'
import { useCallback, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import type { DiferencaComissao, LancamentoComissao } from '../../db/tipos'
import { formatarMoeda } from '../../lib/formatos'
import { valorEfetivo } from '../../comissoes/motor'
import {
  mesAnterior, mesSeguinte, rotuloMes, separarPorMes, mesInicial,
  saldoPendente, totalDaBaixa, saldoDaFornecedora, valorConferido,
  somaDevida, somaRecebida, totaisDePendencia,
} from '../../comissoes/conferencia'
import { idsParaTroca, rotuloMesCurto } from '../../comissoes/painelDoMes'
import { saldoDaDiferenca, diferencasEmAberto, diferencaDoTotalPago, acertarDiferenca, linhasDeDiferencaNoPdf } from '../../comissoes/diferencas'
import { converterNumeroBR } from '../../lib/textoBR'
import { arredondar2 } from '../../pedidos/precos'
import {
  montarLinhas, filtrarLinhas, ordenarLinhas, proximaOrdem, textoParaGuardar, opcoesDeFiltro, contarFiltros,
  marcarVisiveis, marcadasForaDoFiltro, mesesComComissao, filtrosDaAba, remarcaAoSalvar, FILTROS_VAZIOS, ORDEM_PADRAO,
  type LinhaConferencia, type Filtros, type Ordem,
} from '../../comissoes/listaDaConferencia'
import type { DadosRelatorio } from '../../comissoes/PdfConferencia'
import { darBaixaEmLote, acertarPendencia } from '../../comissoes/lancamentos'
import { dataISOLocal } from '../../pedidos/precos'
import { confirmar } from '../../componentes/confirmar'
import JanelaValorPago from './JanelaValorPago'
import RodapeDaBaixa from './RodapeDaBaixa'
import JanelaTotalPago from './JanelaTotalPago'

// Spec 2026-10-04 (print dela "Baixa de Comissao"): o visual novo da
// conferencia. A REGRA nao mudou -- baixa, debito/credito e "Pago" continuam nas
// funcoes de conferencia.ts e lancamentos.ts; as linhas, a busca, o filtro e a
// ordem vem de listaDaConferencia.ts.

const ABAS = [
  { chave: 'a-receber', rotulo: 'A receber', icone: '👛' },
  { chave: 'recebidas', rotulo: 'Recebidas', icone: '✓' },
  { chave: 'previstas', rotulo: 'Previstas', icone: '📅' },
  { chave: 'pendencias', rotulo: 'Débitos/Créditos', icone: '⇅' },
] as const

type Aba = (typeof ABAS)[number]['chave']

export default function ConferenciaFornecedora() {
  const { representadaId } = useParams<{ representadaId: string }>()
  const [parametros] = useSearchParams()
  const mesDaUrl = parametros.get('mes')
  const [aba, setAba] = useState<Aba>('a-receber')
  const [mes, setMes] = useState<string | null>(null)
  const mesDeAbertura = useRef<string | null>(null)
  // Guarda quem ela DESmarcou, nao quem esta marcado: as linhas nascem marcadas
  // (decisao dela, "desmarco o que nao veio"), e assim um lancamento que chegar
  // pelo sync no meio da conferencia ja entra selecionado, sem ela tocar nele.
  const [desmarcados, setDesmarcados] = useState<Set<string>>(new Set())
  const [valores, setValores] = useState<Record<string, string>>({})
  const [confirmando, setConfirmando] = useState(false)
  const [confirmandoPago, setConfirmandoPago] = useState<string | null>(null)
  // Dois erros separados, um por acao: como o aviso nao some sozinho, um erro
  // do "Pago" com um so estado reapareceria dentro do rodape da outra aba,
  // onde se leria como falha da baixa em lote -- e vice-versa.
  const [erroBaixa, setErroBaixa] = useState<string | null>(null)
  const [erroAcerto, setErroAcerto] = useState<string | null>(null)
  const navegar = useNavigate()
  // Spec 2026-10-04: busca, filtro e ordem so ESCONDEM/ordenam linhas -- nunca
  // desmarcam (o rodape avisa as marcadas que ficaram fora do filtro).
  const [busca, setBusca] = useState('')
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VAZIOS)
  const [filtroAberto, setFiltroAberto] = useState(false)
  const [ordem, setOrdem] = useState<Ordem>(ORDEM_PADRAO)
  const [janelaDe, setJanelaDe] = useState<string | null>(null)
  const [listaDeMeses, setListaDeMeses] = useState(false)
  // Spec 2026-10-05: o TOTAL que a fabrica pagou (texto digitado), so na tela
  // ate o "Dar baixa" -- como os valores das linhas.
  const [totalPago, setTotalPago] = useState<string | null>(null)
  const [janelaTotal, setJanelaTotal] = useState(false)

  // O espaco que a lista precisa deixar embaixo para o rodape fixo nao cobrir a
  // ultima linha. MEDIDO do proprio rodape, nunca chutado: a barra muda de
  // altura sozinha (ganha a linha de Debito/Credito, o aviso de erro e o bloco
  // de confirmacao) e o nome da fornecedora quebra em mais linhas em tela
  // estreita. O `pb-40` fixo que existia aqui reservava 160px + os 80px do
  // `main`, e a barra chega a ocupar 251px -- medido no celular dela: em
  // 320x568, com uma razao social de 56 letras e a confirmacao aberta, a barra
  // cobria a ultima linha por 19px. Qualquer numero fixo erra de novo na
  // primeira linha nova que a barra ganhar.
  const [alturaDoRodape, setAlturaDoRodape] = useState(0)
  const observadorDoRodape = useRef<ResizeObserver | null>(null)

  // Callback de ref em vez de useEffect: o rodape monta e desmonta conforme a
  // aba e a lista, e este componente tem `return` antecipado no carregamento --
  // um useEffect que dependesse do rodape teria de vir antes dele e ainda assim
  // nao veria o elemento nascer.
  const medirRodape = useCallback((elemento: HTMLDivElement | null) => {
    observadorDoRodape.current?.disconnect()
    observadorDoRodape.current = null
    if (!elemento) {
      setAlturaDoRodape(0)
      return
    }
    const medir = () => {
      // Altura + o quanto ele flutua acima da base (a classe `bottom-16`), lido
      // do proprio elemento: assim o numero nao e repetido aqui, e mexer na
      // classe continua funcionando sozinho.
      const daBase = Number.parseFloat(getComputedStyle(elemento).bottom) || 0
      setAlturaDoRodape(Math.ceil(elemento.getBoundingClientRect().height + daBase))
    }
    medir()
    observadorDoRodape.current = new ResizeObserver(medir)
    observadorDoRodape.current.observe(elemento)
  }, [])

  const representada = useLiveQuery(
    () => (representadaId ? banco.representadas.get(representadaId) : undefined),
    [representadaId],
  )
  const subPedidos = useLiveQuery(
    () =>
      representadaId
        ? banco.sub_pedidos.where('representada_id').equals(representadaId).toArray()
        : [],
    [representadaId],
  )
  const todos = useLiveQuery(() => banco.lancamentos_comissao.toArray(), [])
  const pedidos = useLiveQuery(() => banco.pedidos.toArray(), [])
  const todosSubPedidos = useLiveQuery(() => banco.sub_pedidos.toArray(), [])
  const tabelas = useLiveQuery(() => banco.tabelas_preco.toArray(), [])
  const clientes = useLiveQuery(() => banco.clientes.toArray(), [])
  // Todas, nao so desta fornecedora: a lista de troca inclui quem so tem
  // diferenca em aberto (minor 04/10).
  const todasDiferencas = useLiveQuery(() => banco.diferencas_comissao.toArray(), [])
  const diferencas = useLiveQuery(
    () => (representadaId ? banco.diferencas_comissao.where('representada_id').equals(representadaId).toArray() : []),
    [representadaId],
  )
  // Sem filtrar apagadas, como a tela Comissoes: fornecedora apagada com saldo
  // em aberto continua cobravel -- e a lista de troca tem de bater com a de la.
  const todasRepresentadas = useLiveQuery(() => banco.representadas.toArray(), [])

  // useLiveQuery devolve undefined enquanto carrega -- nunca testar por typeof
  // nem assumir array (armadilha ja registrada neste projeto).
  //
  // Os tres ultimos entram no guard porque rotuloDoPedido depende deles: sem
  // isso, na janela entre um carregamento e outro TODA linha se chama '…', e
  // com ela o nome acessivel da caixinha e do campo de valor. Lista vazia e
  // array (truthy), entao o guard so espera o carregamento, nao a existencia.
  if (!representada || !subPedidos || !todos || !pedidos || !todosSubPedidos || !tabelas || !clientes || !todasRepresentadas || !diferencas || !todasDiferencas) {
    return <p className="p-4 text-sm text-slate-500">Carregando…</p>
  }

  // Sub-pedido apagado NAO e filtrado aqui, e a lista de cartoes faz igual --
  // os dois lados precisam concordar, senao o cartao diz "Debito R$ X" e esta
  // tela diz "Nenhum debito ou credito em aberto". A direcao do alinhamento vem
  // de lancamentos.ts:44-61: quando a venda cai, o lancamento JA RECEBIDO e
  // mantido de proposito para a divida com a fabrica nao sumir (achado dela no
  // 2026-0014). Filtrar por sub-pedido desfazia essa defesa aqui dentro. O
  // lancamento em si continua filtrado por `apagado_em` logo abaixo.
  const idsDaFornecedora = new Set(subPedidos.map((s) => s.id))
  const meus = todos.filter(
    (l) => l.apagado_em === null && idsDaFornecedora.has(l.sub_pedido_id),
  )
  const liberadas = meus.filter((l) => l.status === 'liberada')
  const recebidas = meus.filter((l) => l.status === 'recebida')
  const previstas = meus.filter((l) => l.status === 'prevista')
  const pendencias = recebidas.filter((l) => saldoPendente(l) !== 0)

  // O mes de abertura e calculado UMA VEZ, na primeira vez que os dados chegam,
  // e depois so muda pelas setas. Se ficasse derivado a cada render, a baixa da
  // Task 7 esvaziaria `liberadas` e o filtro pularia sozinho para outro mes: ela
  // daria baixa em agosto e a aba Recebidas diria "Nada recebido neste mes" --
  // numa tela de dinheiro, isso se le como "nao gravou".
  //
  // Nao adianta somar as recebidas ao calculo: uma recebida antiga conta como
  // ATRASADA no mes anterior e prende a abertura la, justo no caso que a decisao
  // dela manda evitar ("fatura um pedido hoje e ve mes vazio enquanto o cartao
  // diz que ha dinheiro"). Medido: com uma recebida de marco, abriria em julho.
  //
  // O mes vem da LISTA quando ela chegou por um cartao: assim o numero do cartao
  // e o do topo desta tela sao sempre do mesmo mes. Entrada direta por URL (os
  // E2E de pedido-entrega fazem isso) cai na regra de sempre.
  //
  // O formato e validado: um parametro estragado nao pode derrubar a tela nem
  // fazer rotuloMes imprimir "undefined/NaN".
  if (mesDeAbertura.current === null) {
    mesDeAbertura.current =
      mesDaUrl && /^\d{4}-\d{2}$/.test(mesDaUrl) ? mesDaUrl : mesInicial(liberadas, new Date())
  }
  const mesEscolhido = mes ?? mesDeAbertura.current
  const { doMes, atrasadas } = separarPorMes(liberadas, mesEscolhido)
  const recebidasDoMes = separarPorMes(recebidas, mesEscolhido).doMes

  const conferiveis = [...atrasadas, ...doMes]
  const selecionadas = conferiveis.filter((l) => !desmarcados.has(l.id))

  function valorDaLinha(lancamento: LancamentoComissao): number {
    return valorConferido(valorEfetivo(lancamento), valores[lancamento.id])
  }

  function alternar(id: string) {
    setDesmarcados((antes) => {
      const novo = new Set(antes)
      if (novo.has(id)) novo.delete(id)
      else novo.add(id)
      return novo
    })
  }

  // As diferencas do mes em aberto (spec 2026-10-05) entram no saldo como as
  // pendencias dos pedidos.
  const difsAbertas = diferencasEmAberto(diferencas, representada.id)
  const saldoDasDifs = arredondar2(difsAbertas.reduce((soma, d) => soma + saldoDaDiferenca(d), 0))
  // O MESMO numero que darBaixaEmLote vai usar para decidir o ajuste gravado.
  const saldoTotal = arredondar2(saldoDaFornecedora(pendencias) + saldoDasDifs)
  const conta = totalDaBaixa(selecionadas.map((l) => valorDaLinha(l)), saldoTotal)
  // O total pago digitado e a diferenca para o calculado (recalculada se ela
  // mexer nas linhas depois -- o pago fica fixo).
  const pagoLido = totalPago === null ? null : converterNumeroBR(totalPago)
  const pago = pagoLido !== null && pagoLido >= 0 ? pagoLido : null
  const diferenca = diferencaDoTotalPago(conta.total, pago)
  // Os dois lados da aba Debitos/Creditos, calculados uma vez so: a tela
  // re-renderiza inteira a cada tecla digitada num campo de valor. As
  // diferencas do mes somam junto.
  const pendenteDosPedidos = totaisDePendencia(pendencias)
  const pendente = {
    aCobrar: arredondar2(pendenteDosPedidos.aCobrar + difsAbertas.reduce((s, d) => s + Math.max(0, saldoDaDiferenca(d)), 0)),
    aDevolver: arredondar2(pendenteDosPedidos.aDevolver + difsAbertas.reduce((s, d) => s + Math.max(0, -saldoDaDiferenca(d)), 0)),
  }
  const qtdPendencias = pendencias.length + difsAbertas.length

  // Os dois caminhos que criam divida sao diferentes para ela: na venda
  // cancelada volta tudo, na devolucao volta uma parte. Dizer "devolução" nos
  // dois casos mandaria procurar no lugar errado. Ja existia na tela plana e
  // nao pode se perder na mudanca.
  function motivoDaPendencia(lancamento: LancamentoComissao): string | undefined {
    if (saldoPendente(lancamento) >= 0) return undefined
    const subPedido = todosSubPedidos?.find((s) => s.id === lancamento.sub_pedido_id)
    return subPedido?.status === 'cancelado'
      ? 'a venda foi cancelada depois do recebimento'
      : 'houve devolução depois do recebimento'
  }

  // Nada e gravado ate aqui: o valor corrigido na linha vale so para a baixa.
  // O erro NAO some sozinho -- mesma regra da confirmacao ao salvar ja adotada
  // no projeto, porque numa tela de dinheiro um aviso que pisca e some equivale
  // a nao ter avisado.
  async function aoDarBaixa() {
    setErroBaixa(null)
    const gravados = selecionadas.map((lancamento) => lancamento.id)
    try {
      await darBaixaEmLote(
        selecionadas.map((lancamento) => ({
          lancamento, valorRecebido: valorDaLinha(lancamento),
        })),
        pendencias,
        dataISOLocal(new Date()),
        {
          diferencas: difsAbertas,
          totalPago: pago !== null ? { valor: pago, mes: mesEscolhido, representadaId: representada!.id } : undefined,
        },
      )
      setTotalPago(null)
      // O que ela DESMARCOU continua desmarcado. Zerar o set devolveria a linha
      // marcada -- e a linha desmarcada e justamente a que NAO foi gravada,
      // entao ela reapareceria selecionada, com o rodape convidando a dar baixa
      // no dinheiro que ela acabou de dizer que nao chegou.
      //
      // So o texto digitado das linhas GRAVADAS e descartado; elas sairam da
      // lista, e o que ficou continua com a correcao dela na tela.
      setValores((antes) => {
        const resto = { ...antes }
        for (const id of gravados) delete resto[id]
        return resto
      })
      setConfirmando(false)
    } catch {
      setErroBaixa('Não deu para dar baixa. Tente de novo.')
    }
  }

  async function aoMarcarPagoDiferenca(d: DiferencaComissao) {
    setErroAcerto(null)
    try {
      await acertarDiferenca(d, dataISOLocal(new Date()))
      setConfirmandoPago(null)
    } catch {
      setErroAcerto('Não deu para registrar o acerto. Tente de novo.')
    }
  }

  // Trocar de mes zera o total pago: ele era o pagamento daquele mes.
  function irParaMes(m: string) {
    setMes(m)
    setTotalPago(null)
  }

  async function aoMarcarPago(lancamento: LancamentoComissao) {
    setErroAcerto(null)
    try {
      await acertarPendencia(lancamento, dataISOLocal(new Date()))
      setConfirmandoPago(null)
    } catch {
      setErroAcerto('Não deu para registrar o acerto. Tente de novo.')
    }
  }

  // ── Linhas (spec 2026-10-04) ──────────────────────────────────────────────
  const ctx = { subPedidos: todosSubPedidos, pedidos, tabelas, clientes }
  const idsAtrasadas = new Set(atrasadas.map((l) => l.id))
  // Cada aba so usa os filtros que fazem sentido nela (revisao final 04/10).
  const filtrosAqui = filtrosDaAba(filtros, aba)
  const verLinhas = (linhas: LinhaConferencia[]) =>
    ordenarLinhas(filtrarLinhas(linhas, filtrosAqui, busca, idsAtrasadas), ordem)
  const linhasAtrasadas = verLinhas(montarLinhas(atrasadas, ctx, valorDaLinha))
  const linhasDoMes = verLinhas(montarLinhas(doMes, ctx, valorDaLinha))
  const linhasRecebidas = verLinhas(montarLinhas(recebidasDoMes, ctx, (l) => l.valor_recebido ?? 0))
  const linhasPrevistas = verLinhas(montarLinhas(previstas, ctx, valorEfetivo))
  const linhasPendencias = verLinhas(montarLinhas(pendencias, ctx, () => 0))
  const todasDaAba: LinhaConferencia[] =
    aba === 'a-receber' ? montarLinhas(conferiveis, ctx, valorDaLinha)
      : aba === 'recebidas' ? montarLinhas(recebidasDoMes, ctx, (l) => l.valor_recebido ?? 0)
        : aba === 'previstas' ? montarLinhas(previstas, ctx, valorEfetivo)
          : montarLinhas(pendencias, ctx, () => 0)
  const visiveis: LinhaConferencia[] =
    aba === 'a-receber' ? [...linhasAtrasadas, ...linhasDoMes]
      : aba === 'recebidas' ? linhasRecebidas : aba === 'previstas' ? linhasPrevistas : linhasPendencias
  const opcoes = opcoesDeFiltro(todasDaAba)
  const nFiltros = contarFiltros(filtrosAqui)
  const visiveisIds = visiveis.map((l) => l.id)
  const todasVisiveisMarcadas = visiveisIds.length > 0 && visiveisIds.every((id) => !desmarcados.has(id))
  const foraDoFiltro = marcadasForaDoFiltro(conferiveis.map((l) => l.id), desmarcados, new Set(visiveisIds))
  const linhaDaJanela = janelaDe ? montarLinhas(conferiveis.filter((l) => l.id === janelaDe), ctx, valorDaLinha)[0] : undefined

  // A lista de troca e a MESMA da tela Comissoes (regra em painelDoMes.ts).
  const idsDaTroca = idsParaTroca({
    atual: representada.id, mes: mesEscolhido, representadas: todasRepresentadas,
    lancamentos: todos, subPedidos: todosSubPedidos, pedidos, diferencas: todasDiferencas,
  })
  const paraTroca = todasRepresentadas
    .filter((r) => idsDaTroca.has(r.id))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))

  async function trocarFornecedora(id: string) {
    if (id === representada!.id) return
    const editou = Object.keys(valores).length > 0 || desmarcados.size > 0 || totalPago !== null
    if (editou && !(await confirmar('Descartar os valores digitados e as marcações desta fornecedora?', { botao: 'Descartar', perigo: true }))) return
    // A rota reaproveita este componente: o que era desta fornecedora sai.
    setDesmarcados(new Set())
    setValores({})
    setTotalPago(null)
    setBusca('')
    setFiltros(FILTROS_VAZIOS)
    setConfirmando(false)
    setJanelaDe(null)
    // Revisao final 04/10: um erro da baixa da fornecedora anterior apareceria
    // no rodape da nova -- e se leria como falha DELA.
    setErroBaixa(null)
    setErroAcerto(null)
    setConfirmandoPago(null)
    setFiltroAberto(false)
    setListaDeMeses(false)
    navegar(`/comissoes/${id}?mes=${mesEscolhido}`, { replace: true })
  }

  function salvarDaJanela(id: string, digitado: string | undefined, devido: number) {
    const texto = textoParaGuardar(digitado, devido)
    const mudou = remarcaAoSalvar(valores[id], texto, devido)
    setValores((antes) => {
      const novo = { ...antes }
      if (texto === undefined) delete novo[id]
      else novo[id] = texto
      return novo
    })
    // Corrigiu o valor: e porque veio, a linha fica marcada. Sem mudar o valor
    // nao remarca -- a linha que ela desmarcou continua fora da baixa.
    if (mudou) setDesmarcados((antes) => {
      const novo = new Set(antes)
      novo.delete(id)
      return novo
    })
    setJanelaDe(null)
  }

  const ROTULO_DA_ABA = { 'a-receber': 'A receber', recebidas: 'Recebidas', previstas: 'Previstas', pendencias: 'Débitos/Créditos' }
  function gerarDados(): DadosRelatorio {
    const descricoes: string[] = []
    if (filtros.situacao !== 'todas') descricoes.push(filtros.situacao === 'ajuste' ? 'Só Ajuste' : 'Só Normal')
    if (filtros.soAtrasadas) descricoes.push('Só atrasadas')
    if (filtros.clienteId) descricoes.push(`Cliente: ${opcoes.clientes.find((c) => c.id === filtros.clienteId)?.nome ?? ''}`)
    if (filtros.tabelaId) descricoes.push(`Tabela: ${opcoes.tabelas.find((t) => t.id === filtros.tabelaId)?.nome ?? ''}`)
    if (busca.trim()) descricoes.push(`Busca: "${busca.trim()}"`)
    const totais =
      aba === 'a-receber'
        ? [
          { rotulo: 'Total a receber', valor: somaDevida(conferiveis) },
          { rotulo: `Selecionados (${selecionadas.length})`, valor: conta.selecionados },
          { rotulo: rotuloDoAjuste(conta.ajuste), valor: conta.ajuste, comSinal: true },
          { rotulo: 'Total da baixa', valor: conta.total },
          ...(pago !== null
            ? [{ rotulo: 'Diferença do mês', valor: diferenca, comSinal: true }, { rotulo: 'Total pago pela fábrica', valor: pago }]
            : []),
        ]
        : aba === 'recebidas' ? [{ rotulo: 'Recebido no mês', valor: somaRecebida(recebidasDoMes) }]
          : aba === 'previstas' ? [{ rotulo: 'Total previsto', valor: somaDevida(previstas) }]
            : [{ rotulo: 'A cobrar', valor: pendente.aCobrar }, { rotulo: 'A devolver', valor: pendente.aDevolver }]
    // Revisao final 04/10: na A receber o PDF diz quais linhas entram na baixa e
    // quantas marcadas ficaram fora do filtro -- senao os totais nao fecham com
    // as linhas listadas.
    const naBaixa = aba === 'a-receber' ? selecionadas.map((l) => l.id) : null
    const notas = aba === 'a-receber' && foraDoFiltro > 0
      ? [`${foraDoFiltro} ${foraDoFiltro === 1 ? 'marcada fora do filtro entra' : 'marcadas fora do filtro entram'} na baixa`]
      : []
    return {
      fornecedora: representada!.nome, mes: mesEscolhido, aba: ROTULO_DA_ABA[aba],
      geradoEm: new Date().toLocaleDateString('pt-BR'), filtros: descricoes, linhas: visiveis, totais, naBaixa, notas,
      diferencas: aba === 'pendencias' ? linhasDeDiferencaNoPdf(difsAbertas) : undefined,
    }
  }

  const comMes = aba === 'a-receber' || aba === 'recebidas'

  return (
    // O padding de baixo e o do rodape MEDIDO (medirRodape), nunca chutado.
    <section
      className="p-4 space-y-3"
      style={alturaDoRodape > 0 ? { paddingBottom: alturaDoRodape } : undefined}
    >
      <Link to="/comissoes" className="text-sm font-semibold text-teal-700">← Voltar</Link>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-xl font-extrabold text-slate-900 leading-tight">Baixa de Comissão</h2>
          <p className="text-sm text-slate-600 truncate">{representada.nome}</p>
        </div>
        <label className="shrink-0 max-w-[48%]">
          <span className="sr-only">Trocar de fornecedora</span>
          <select
            data-testid="trocar-fornecedora"
            value={representada.id}
            onChange={(e) => void trocarFornecedora(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-2 py-2 text-sm font-semibold text-slate-800 shadow-sm"
          >
            {paraTroca.map((r) => <option key={r.id} value={r.id}>🏢 {r.nome}</option>)}
          </select>
        </label>
      </div>

      <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
        {ABAS.map((definicao) => (
          <button
            key={definicao.chave}
            data-testid={`aba-${definicao.chave}`}
            onClick={() => setAba(definicao.chave)}
            className={`shrink-0 rounded-xl px-3 py-2 text-sm flex items-center gap-1.5 shadow-sm ${
              aba === definicao.chave ? 'bg-teal-800 text-white font-semibold' : 'bg-white text-slate-700'}`}
          >
            <span aria-hidden="true">{definicao.icone}</span>
            {definicao.rotulo}
            {definicao.chave === 'pendencias' && qtdPendencias > 0 && ` (${qtdPendencias})`}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        {comMes ? (
          <div className="relative flex-1 min-w-0 flex items-center justify-between bg-white rounded-xl shadow-sm p-1.5">
            <button aria-label="Mês anterior" onClick={() => irParaMes(mesAnterior(mesEscolhido))} className="px-2 py-1 text-lg text-slate-600">‹</button>
            <button
              type="button" onClick={() => setListaDeMeses((a) => !a)} aria-haspopup="listbox"
              className="min-w-0 flex items-center gap-1 text-sm font-semibold text-slate-800"
            >
              <span aria-hidden="true">📅</span>
              <span data-testid="mes-atual" className="truncate">{rotuloMes(mesEscolhido)}</span>
              <span aria-hidden="true" className="text-slate-500">▾</span>
            </button>
            <button aria-label="Mês seguinte" onClick={() => irParaMes(mesSeguinte(mesEscolhido))} className="px-2 py-1 text-lg text-slate-600">›</button>
            {listaDeMeses && (
              <ul role="listbox" aria-label="Escolher o mês" className="absolute z-20 top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-xl bg-white shadow-lg border border-slate-200">
                {mesesComComissao(meus, mesEscolhido).map((m) => (
                  <li key={m}>
                    <button
                      type="button" role="option" aria-selected={m === mesEscolhido}
                      onClick={() => { irParaMes(m); setListaDeMeses(false) }}
                      className={`block w-full text-left px-3 py-2 text-sm ${m === mesEscolhido ? 'bg-teal-50 font-semibold text-teal-900' : 'text-slate-700'}`}
                    >
                      {rotuloMes(m)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : <div className="flex-1" />}
        <button
          type="button" data-testid="botao-filtrar" onClick={() => setFiltroAberto((a) => !a)}
          className={`shrink-0 rounded-xl px-3 py-2 text-sm font-semibold shadow-sm ${nFiltros > 0 ? 'bg-teal-800 text-white' : 'bg-white text-slate-700'}`}
        >
          ⏷ Filtrar{nFiltros > 0 ? ` (${nFiltros})` : ''}
        </button>
      </div>

      {filtroAberto && (
        <div data-testid="painel-filtro" className="bg-white rounded-xl shadow-sm p-3 space-y-3 text-sm">
          <div>
            <p className="text-xs text-slate-500 mb-1">Situação</p>
            <div className="grid grid-cols-3 gap-1">
              {(['todas', 'normal', 'ajuste'] as const).map((s) => (
                <button
                  key={s} type="button" onClick={() => setFiltros({ ...filtros, situacao: s })}
                  className={`rounded-lg py-1.5 ${filtros.situacao === s ? 'bg-teal-800 text-white font-semibold' : 'bg-slate-100 text-slate-700'}`}
                >
                  {s === 'todas' ? 'Todas' : s === 'normal' ? 'Normal' : 'Ajuste'}
                </button>
              ))}
            </div>
          </div>
          {aba === 'a-receber' && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={filtros.soAtrasadas} onChange={(e) => setFiltros({ ...filtros, soAtrasadas: e.target.checked })} className="size-4 accent-teal-700" />
              Só atrasadas
            </label>
          )}
          <label className="block">
            <span className="text-xs text-slate-500">Cliente</span>
            <select
              value={filtros.clienteId ?? ''} onChange={(e) => setFiltros({ ...filtros, clienteId: e.target.value || null })}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5"
            >
              <option value="">Todos</option>
              {opcoes.clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Tabela</span>
            <select
              value={filtros.tabelaId ?? ''} onChange={(e) => setFiltros({ ...filtros, tabelaId: e.target.value || null })}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5"
            >
              <option value="">Todas</option>
              {opcoes.tabelas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          </label>
          <div className="flex justify-between">
            <button type="button" onClick={() => setFiltros(FILTROS_VAZIOS)} className="text-teal-700 underline underline-offset-2">Limpar</button>
            <button type="button" onClick={() => setFiltroAberto(false)} className="rounded-lg bg-teal-800 px-3 py-1.5 font-semibold text-white">Ver</button>
          </div>
        </div>
      )}

      {aba === 'a-receber' && (
        // `conferiveis` = atrasadas + do mes, o MESMO par que a lista empilha e
        // que o rodape soma. "Total a receber" e o DEVIDO, nao o corrigido: e a
        // referencia contra o extrato, nao se mexe quando ela digita ou
        // desmarca (ela, 2026-08-01). Quem responde pela selecao e o rodape.
        <div className="grid grid-cols-2 gap-2">
          <Quadro fundo="bg-emerald-50" icone="💰" rotulo="Total a receber" testid="total-topo" valor={formatarMoeda(somaDevida(conferiveis))} />
          <Quadro fundo="bg-slate-50 border border-slate-200" icone="📄" rotulo="Selecionados" valor={`${selecionadas.length} ${selecionadas.length === 1 ? 'item' : 'itens'}`} />
          <Quadro
            fundo={conta.ajuste < 0 ? 'bg-red-50' : conta.ajuste > 0 ? 'bg-sky-50' : 'bg-slate-50 border border-slate-200'}
            icone="⇅" rotulo={conta.ajuste > 0 ? 'Créditos' : conta.ajuste < 0 ? 'Débitos' : 'Débitos/Créditos'}
            cor={conta.ajuste < 0 ? 'text-red-700' : conta.ajuste > 0 ? 'text-sky-700' : 'text-slate-500'}
            valor={valorComSinal(conta.ajuste)}
          />
          {/* Spec 2026-10-05: tocar no Total da baixa abre a janela do total pago. */}
          <button type="button" data-testid="abrir-total-pago" onClick={() => setJanelaTotal(true)} className="text-left min-w-0">
            {pago === null ? (
              <Quadro fundo="bg-teal-50" icone="📊" rotulo="Total da baixa ✎" cor="text-teal-900" valor={formatarMoeda(conta.total)} />
            ) : (
              <span className="block rounded-xl p-3 bg-teal-50">
                <span className="block text-xs text-slate-600">📊 Total pago ✎</span>
                <span className="block text-base font-bold text-teal-900 break-words">{formatarMoeda(pago)}</span>
                <span className={`block text-[11px] ${diferenca > 0 ? 'text-sky-700' : diferenca < 0 ? 'text-red-700' : 'text-slate-500'}`}>
                  Diferença do mês {sinal(diferenca)}{formatarMoeda(Math.abs(diferenca))}
                </span>
              </span>
            )}
          </button>
        </div>
      )}
      {aba === 'recebidas' && (
        // "A funcao do total na aba recebidos e a conferencia do que ele recebeu
        // nas empresas" -- ela. E o valor GRAVADO, nao o devido.
        <TotalDoTopo rotulo="Recebido no mês" valor={somaRecebida(recebidasDoMes)} testid="total-topo" />
      )}
      {aba === 'previstas' && (
        <>
          <TotalDoTopo rotulo="Total previsto" valor={somaDevida(previstas)} testid="total-topo" />
          <p className="text-xs text-slate-500">Ainda não faturadas — é expectativa, não há o que conferir.</p>
        </>
      )}
      {aba === 'pendencias' && (
        // DUAS linhas, nunca uma soma: +100 com -100 diria "R$ 0,00" com as duas
        // dividas listadas logo abaixo. So aparece o lado que existe.
        <>
          {pendente.aCobrar > 0 && <TotalDoTopo rotulo="A cobrar" valor={pendente.aCobrar} testid="total-topo-cobrar" />}
          {pendente.aDevolver > 0 && <TotalDoTopo rotulo="A devolver" valor={pendente.aDevolver} testid="total-topo-devolver" />}
        </>
      )}

      <label className="flex items-center gap-2 bg-white rounded-xl shadow-sm px-3 py-2">
        <span aria-hidden="true" className="text-slate-500">🔍</span>
        <input
          data-testid="busca-conferencia" value={busca} onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por pedido, cliente ou tabela"
          className="min-w-0 flex-1 text-sm outline-none bg-transparent"
        />
        {busca && <button type="button" aria-label="Limpar busca" onClick={() => setBusca('')} className="text-slate-500 text-lg leading-none">×</button>}
      </label>

      <div className="flex items-center gap-2 rounded-xl bg-slate-200/60 px-3 py-2 text-xs font-semibold text-slate-600">
        {aba === 'a-receber' && (
          <input
            type="checkbox" aria-label="Marcar todos" checked={todasVisiveisMarcadas}
            onChange={(e) => setDesmarcados(marcarVisiveis(desmarcados, visiveisIds, e.target.checked))}
            className="size-5 accent-teal-700 shrink-0"
          />
        )}
        <BotaoOrdem rotulo="Pedido" coluna="pedido" ordem={ordem} setOrdem={setOrdem} className="w-[30%]" />
        <BotaoOrdem rotulo="Descrição" coluna="descricao" ordem={ordem} setOrdem={setOrdem} className="flex-1" />
        <BotaoOrdem rotulo="Valor" coluna="valor" ordem={ordem} setOrdem={setOrdem} className="justify-end" />
      </div>
      {/* Minor 04/10, item 7: sem isto, depois de tocar outra coluna nao havia
          como voltar a ordem por data sem sair da tela. */}
      {(ordem.coluna !== ORDEM_PADRAO.coluna || ordem.sentido !== ORDEM_PADRAO.sentido) && (
        <button
          type="button" onClick={() => setOrdem(ORDEM_PADRAO)}
          className="-mt-2 block ml-auto text-xs text-teal-700 underline underline-offset-2"
        >
          Voltar à ordem por data
        </button>
      )}

      {aba === 'a-receber' && (
        <>
          {linhasAtrasadas.length > 0 && (
            <div data-testid="grupo-atrasadas" className="space-y-2">
              <p className="text-xs font-medium text-amber-800">Ainda em aberto de meses anteriores</p>
              {linhasAtrasadas.map((linha) => (
                <Linha
                  key={linha.id} linha={linha} etiqueta
                  tarja={`liberada em ${rotuloMes(linha.mesOrigem ?? mesEscolhido)}`}
                  marcada={!desmarcados.has(linha.id)} aoAlternar={() => alternar(linha.id)} aoAbrir={() => setJanelaDe(linha.id)}
                />
              ))}
            </div>
          )}
          {linhasDoMes.map((linha) => (
            <Linha
              key={linha.id} linha={linha} etiqueta
              marcada={!desmarcados.has(linha.id)} aoAlternar={() => alternar(linha.id)} aoAbrir={() => setJanelaDe(linha.id)}
            />
          ))}
          {doMes.length === 0 && atrasadas.length === 0 && <p className="text-sm text-slate-500">Nada a receber neste mês.</p>}
        </>
      )}

      {aba === 'recebidas' && (
        <>
          {linhasRecebidas.map((linha) => (
            <Linha
              key={linha.id} linha={linha} etiqueta
              tarja={`recebido em ${linha.lancamento.data_recebimento ?? ''}`}
              saldo={saldoPendente(linha.lancamento)} motivo={motivoDaPendencia(linha.lancamento)}
            />
          ))}
          {recebidasDoMes.length === 0 && <p className="text-sm text-slate-500">Nada recebido neste mês.</p>}
        </>
      )}

      {aba === 'previstas' && (
        <>
          {linhasPrevistas.map((linha) => <Linha key={linha.id} linha={linha} />)}
          {previstas.length === 0 && <p className="text-sm text-slate-500">Nenhuma comissão prevista.</p>}
        </>
      )}

      {aba === 'pendencias' && (
        <>
          {linhasPendencias.map((linha) => {
            const lancamento = linha.lancamento
            return (
              <div key={linha.id} className="space-y-1">
                <Linha linha={linha} semValor saldo={saldoPendente(lancamento)} motivo={motivoDaPendencia(lancamento)} />
                {/* Confirmacao tambem aqui: o acerto e irreversivel pela tela --
                    soma em valor_acertado, o saldo vai a zero e a linha some. */}
                {confirmandoPago === lancamento.id ? (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-700">
                      {saldoPendente(lancamento) < 0
                        ? `Devolveu ${formatarMoeda(-saldoPendente(lancamento))} para ${representada.nome}, por fora?`
                        : `Recebeu ${formatarMoeda(saldoPendente(lancamento))} de ${representada.nome}, por fora?`}
                    </p>
                    <div className="flex gap-2">
                      <button data-testid="confirmar-pago" onClick={() => void aoMarcarPago(lancamento)} className="flex-1 bg-teal-700 text-white rounded-lg py-2 text-sm font-medium">
                        Confirmar
                      </button>
                      <button onClick={() => setConfirmandoPago(null)} className="flex-1 bg-white border text-slate-600 rounded-lg py-2 text-sm">
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button data-testid="botao-pago" onClick={() => setConfirmandoPago(lancamento.id)} className="w-full bg-teal-700 text-white rounded-lg py-2 text-sm">
                    Pago
                  </button>
                )}
              </div>
            )
          })}
          {/* Sem isto uma falha do "Pago" ficaria gravada no estado e invisivel. */}
          {erroAcerto && <CartaoErro titulo="Não foi possível confirmar">{erroAcerto}</CartaoErro>}
          {/* Spec 2026-10-05: a diferenca do mes, com o mesmo "Pago". */}
          {difsAbertas.map((d) => {
            const saldoDela = saldoDaDiferenca(d)
            return (
              <div key={d.id} className="space-y-1">
                <div data-testid="linha-diferenca" className="bg-white rounded-xl shadow-sm p-3">
                  <p className="text-sm font-bold text-slate-900">Diferença do mês · ref. {rotuloMesCurto(d.mes)}</p>
                  <p className="text-xs text-slate-500">do total pago na baixa de {d.data.slice(8, 10)}/{d.data.slice(5, 7)}/{d.data.slice(0, 4)}</p>
                  {saldoDela > 0 ? (
                    <span className="inline-block mt-1 text-xs bg-sky-50 text-sky-700 rounded px-2 py-0.5">⚠ faltam {formatarMoeda(saldoDela)} — cobrar</span>
                  ) : (
                    <span className="inline-block mt-1 text-xs bg-red-50 text-red-700 rounded px-2 py-0.5">A devolver {formatarMoeda(-saldoDela)} — diferença do total pago</span>
                  )}
                </div>
                {confirmandoPago === d.id ? (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-700">
                      {saldoDela < 0
                        ? `Devolveu ${formatarMoeda(-saldoDela)} para ${representada.nome}, por fora?`
                        : `Recebeu ${formatarMoeda(saldoDela)} de ${representada.nome}, por fora?`}
                    </p>
                    <div className="flex gap-2">
                      <button data-testid="confirmar-pago" onClick={() => void aoMarcarPagoDiferenca(d)} className="flex-1 bg-teal-700 text-white rounded-lg py-2 text-sm font-medium">
                        Confirmar
                      </button>
                      <button onClick={() => setConfirmandoPago(null)} className="flex-1 bg-white border text-slate-600 rounded-lg py-2 text-sm">
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button data-testid="botao-pago" onClick={() => setConfirmandoPago(d.id)} className="w-full bg-teal-700 text-white rounded-lg py-2 text-sm">
                    Pago
                  </button>
                )}
              </div>
            )
          })}
          {qtdPendencias === 0 && <p className="text-sm text-slate-500">Nenhum débito ou crédito em aberto.</p>}
        </>
      )}

      {todasDaAba.length > 0 && visiveis.length === 0 && (
        <p data-testid="filtro-vazio" className="text-sm text-slate-500">
          Nenhuma linha neste filtro.{' '}
          <button type="button" onClick={() => { setFiltros(FILTROS_VAZIOS); setBusca('') }} className="text-teal-700 underline underline-offset-2">Limpar</button>
        </p>
      )}

      {linhaDaJanela && (
        <JanelaValorPago
          rotulo={linhaDaJanela.rotulo}
          devido={linhaDaJanela.devido}
          texto={valores[linhaDaJanela.id]}
          aoSalvar={(texto) => salvarDaJanela(linhaDaJanela.id, texto, linhaDaJanela.devido)}
          aoFechar={() => setJanelaDe(null)}
        />
      )}

      {janelaTotal && (
        <JanelaTotalPago
          calculado={conta.total}
          texto={totalPago}
          aoSalvar={(texto) => { setTotalPago(texto); setJanelaTotal(false) }}
          aoFechar={() => setJanelaTotal(false)}
        />
      )}

      {(aba !== 'a-receber' || conferiveis.length > 0) && (
        <RodapeDaBaixa
          key={representada.id}
          refDoRodape={medirRodape}
          comBaixa={aba === 'a-receber'}
          conta={conta}
          quantidade={selecionadas.length}
          foraDoFiltro={foraDoFiltro}
          fornecedora={representada.nome}
          confirmando={confirmando}
          setConfirmando={setConfirmando}
          aoDarBaixa={aoDarBaixa}
          erroBaixa={erroBaixa}
          pagoEDiferenca={pago !== null ? { pago, diferenca } : null}
          aoAbrirTotal={() => setJanelaTotal(true)}
          gerarDados={gerarDados}
        />
      )}
    </section>
  )
}

// Os totais das outras abas: a soma geral do que esta na tela, sem olhar
// marcacao (ela separou os papeis: topo = referencia, rodape = o que grava).
function TotalDoTopo({ rotulo, valor, testid }: { rotulo: string; valor: number; testid: string }) {
  return (
    <div data-testid={testid} className="flex justify-between bg-white rounded-xl shadow-sm px-3 py-2">
      <span className="text-sm text-slate-600">{rotulo}</span>
      <span className="text-sm font-bold text-slate-800">{formatarMoeda(valor)}</span>
    </div>
  )
}

function Quadro({ fundo, icone, rotulo, valor, cor = 'text-slate-900', testid }: {
  fundo: string; icone: string; rotulo: string; valor: string; cor?: string; testid?: string
}) {
  return (
    <div data-testid={testid} className={`rounded-xl p-3 min-w-0 ${fundo}`}>
      <p className="text-xs text-slate-600"><span aria-hidden="true">{icone}</span> {rotulo}</p>
      <p className={`text-base font-bold break-words ${cor}`}>{valor}</p>
    </div>
  )
}

function BotaoOrdem({ rotulo, coluna, ordem, setOrdem, className = '' }: {
  rotulo: string; coluna: Ordem['coluna']; ordem: Ordem; setOrdem: (o: Ordem) => void; className?: string
}) {
  const ativa = ordem.coluna === coluna
  return (
    <button
      type="button" onClick={() => setOrdem(proximaOrdem(ordem, coluna))}
      aria-label={`Ordenar por ${rotulo}`}
      className={`flex items-center gap-1 ${ativa ? 'text-teal-900' : ''} ${className}`}
    >
      {rotulo}<span aria-hidden="true">{ativa ? (ordem.sentido === 'asc' ? '↑' : '↓') : '⇅'}</span>
    </button>
  )
}


const dataBR = (iso: string) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '')

function Linha({ linha, marcada, aoAlternar, aoAbrir, etiqueta = false, semValor = false, tarja, saldo = 0, motivo }: {
  linha: LinhaConferencia
  marcada?: boolean
  aoAlternar?: () => void
  aoAbrir?: () => void
  etiqueta?: boolean
  semValor?: boolean
  tarja?: string
  saldo?: number
  motivo?: string
}) {
  const miolo = (
    <>
      <span className="w-[30%] shrink-0 min-w-0">
        <span className="block text-sm font-bold text-slate-900 truncate">{linha.pedido}</span>
        <span className="block text-xs text-slate-500">{dataBR(linha.data)}</span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-slate-800 truncate">{linha.tabela ?? '—'}</span>
        {linha.cliente && <span className="block text-xs text-slate-500 truncate">{linha.cliente}</span>}
      </span>
      {!semValor && (
        <span className="shrink-0 text-right">
          <span className="block text-sm font-bold text-slate-900">{formatarMoeda(linha.valor)}</span>
          {etiqueta && (
            <span className={`inline-block mt-0.5 rounded-full px-2 text-[11px] ${linha.ajuste ? 'bg-amber-100 text-amber-800' : 'bg-sky-50 text-sky-700'}`}>
              {linha.ajuste ? 'Ajuste' : 'Normal'}
            </span>
          )}
        </span>
      )}
      {aoAbrir && <span aria-hidden="true" className="shrink-0 text-slate-400 text-lg">›</span>}
    </>
  )
  return (
    <div data-testid="linha-lancamento" className="bg-white rounded-xl shadow-sm p-3">
      <div className="flex items-center gap-2">
        {aoAlternar && (
          <input
            type="checkbox" aria-label={linha.rotulo} checked={marcada} onChange={aoAlternar}
            className="size-5 accent-teal-700 shrink-0"
          />
        )}
        {aoAbrir ? (
          <button type="button" onClick={aoAbrir} aria-label={`Corrigir valor de ${linha.rotulo}`} className="min-w-0 flex-1 flex items-center gap-2 text-left">
            {miolo}
          </button>
        ) : (
          <div className="min-w-0 flex-1 flex items-center gap-2">{miolo}</div>
        )}
      </div>
      {tarja && <span className="block mt-1 text-xs text-slate-500">{tarja}</span>}
      {/* Os dois caminhos que criam divida sao diferentes para ela: o motivo diz
          onde procurar. */}
      {saldo < 0 && (
        <span className="inline-block mt-1 text-xs bg-red-50 text-red-700 rounded px-2 py-0.5">
          A devolver {formatarMoeda(-saldo)}{motivo ? ` — ${motivo}` : ''}
        </span>
      )}
      {saldo > 0 && (
        <span className="inline-block mt-1 text-xs bg-sky-50 text-sky-700 rounded px-2 py-0.5">
          ⚠ faltam {formatarMoeda(saldo)} — cobrar
        </span>
      )}
    </div>
  )
}
